import {
    Box,
    Container,
    Button,
    Typography,
    Card,
    CircularProgress,
    TextField,
    Alert,
    Stack,
    InputAdornment,
    IconButton
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useResetPassword } from '../../react-query/auth-queries';
import { useState } from 'react';
import { ROUTES } from '../../router/routes';
import { brand } from '../../shared-theme/themePrimitives';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { PodcastIcon } from '@/components/icons/PodcastIcon';

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const { mutateAsync: resetPassword, isPending } = useResetPassword();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage('');
        setError('');
        
        if (!token) {
            setError('Invalid or missing reset token.');
            return;
        }

        try {
            const response = await resetPassword({ token, newPassword });
            if (response.success) {
                setMessage('Password reset successfully. You can now login.');
                setTimeout(() => {
                    navigate(ROUTES.LOGIN.path);
                }, 3000);
            } else {
                setError(response.message || 'Reset failed');
            }
        } catch (err: any) {
            console.error("Reset password error:", err);
            setError(err.response?.data?.message || 'An error occurred');
        }
    };

    if (!token) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Alert severity="error">Missing reset token. Please check your email link.</Alert>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(ellipse at 50% -20%, rgba(210, 225, 255, 0.5) 0%, rgba(255,255,255,0) 70%)',
                bgcolor: '#FAFAFA',
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="sm" >
                <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                        <PodcastIcon size={48} color={brand[500]} />
                    </Box>
                    <Typography variant="h3" component="h1" gutterBottom fontWeight={800} sx={{ color: '#0F172A', fontSize: { xs: '1.75rem', md: '2rem' } }}>
                        Reset Password
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center">
                        Enter your new password below
                    </Typography>
                </Box>

                <Card 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 3, sm: 5 }, 
                        borderRadius: '24px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                    }}
                >
                    {message && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{message}</Alert>}
                    {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}
                    
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2.5}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                    New Password
                                </Typography>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="newPassword"
                                    placeholder="••••••••"
                                    type={showPassword ? 'text' : 'password'}
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' }, mt: 0 }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={isPending}
                                sx={{ 
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    bgcolor: brand[400],
                                    '&:hover': { bgcolor: brand[500] },
                                    boxShadow: '0 10px 20px -10px rgba(225, 89, 27, 0.4)'
                                }}
                            >
                                {isPending ? <CircularProgress size={24} color="inherit" /> : 'Set New Password'}
                            </Button>
                        </Stack>
                    </Box>
                </Card>
            </Container>
        </Box>
    );
}
